import { css, keyframes, useTheme } from '@emotion/react';
import type { LexicalEditor } from 'lexical';
import { Check } from 'lucide-react';

import { LexicalScriptEditor } from './lexical/lexical-script-editor';

import type { SaveStatus } from '@/hooks/use-auto-save';
import { fontSizes, spacing } from '@/theme/tokens';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

interface ScriptEditorProps {
  initialContent: string;
  onChange: (value: string) => void;
  onEditorReady?: (editor: LexicalEditor) => void;
  wordCount: number;
  charCount: number;
  sectionCount: number;
  totalDuration?: string;
  timingEstimated?: boolean;
  saveStatus: SaveStatus;
  readOnly?: boolean;
  onExport?: () => void;
}

const STATUS_LABELS: Record<SaveStatus, string> = {
  idle: '',
  saving: 'Saving...',
  saved: 'Saved',
  error: 'Save failed',
};

export function ScriptEditor({
  initialContent,
  onChange,
  onEditorReady,
  wordCount,
  charCount,
  sectionCount,
  saveStatus,
  totalDuration,
  timingEstimated,
  readOnly = false,
  onExport,
}: ScriptEditorProps) {
  const theme = useTheme();

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        height: 100%;
      `}
    >
      <LexicalScriptEditor
        initialContent={initialContent}
        onChange={onChange}
        onEditorReady={onEditorReady}
        readOnly={readOnly}
        onExport={onExport}
      />
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: ${spacing.sm} 0;
          font-size: ${fontSizes.sm};
          color: ${theme.colors.textSecondary};
        `}
      >
        <span>
          {wordCount.toLocaleString()} words | {charCount.toLocaleString()} chars |{' '}
          {sectionCount.toLocaleString()} {sectionCount === 1 ? 'section' : 'sections'}
          {totalDuration && (
            <>
              {' | '}
              {timingEstimated && '~'}
              {totalDuration} presenting time
              {timingEstimated && <em> estimated</em>}
            </>
          )}
        </span>
        {saveStatus !== 'idle' && (
          <span
            key={saveStatus}
            data-testid="save-status"
            css={css`
              display: flex;
              align-items: center;
              gap: 4px;
              color: ${saveStatus === 'error' ? theme.colors.error : theme.colors.textSecondary};
              animation: ${fadeIn} 0.3s ease-out;
            `}
          >
            {saveStatus === 'saved' && (
              <Check
                size={14}
                color="#22c55e"
                strokeWidth={2.5}
                aria-hidden="true"
                data-testid="save-checkmark"
              />
            )}
            {STATUS_LABELS[saveStatus]}
          </span>
        )}
      </div>
    </div>
  );
}
